<script setup lang="ts">
interface Item {
    label: string
    value: any
}

const props = defineProps<{
    modelValue: any
    items: Item[]
    defaultIndex?: number
    loading?: boolean
}>()

const emit = defineEmits<{
    (_event: 'update:modelValue', _value: any): void
}>()

/**
 * Selected tab.
 */
const selectedTab = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

/**
 * Selected tab index.
 */
const selectedTabIndex = computed(() => {
    return props.items.findIndex(item => item.value === props.modelValue)
})

/**
 * On change event.
 */
function onChange(index: number) {
    emit('update:modelValue', props.items[index]?.value)
}

/**
 * Set default index if modelValue is undefined.
 */
function setDefaultIndex() {
    if (props.modelValue === undefined && props.defaultIndex !== undefined) {
        onChange(props.defaultIndex)
    }
}

tryOnBeforeMount(setDefaultIndex)
</script>

<template>
    <div class="flex w-full flex-col">
        <TabGroup
            :default-index="defaultIndex"
            :selected-index="selectedTabIndex"
            @change="onChange"
        >
            <TabList
                class="z-10 flex w-full items-center justify-end space-x-8 bg-white px-4"
            >
                <UiSelect
                    v-model="selectedTab"
                    :options="items"
                    class="block flex-1 p-4 xl:hidden"
                />

                <Tab
                    v-for="(item, index) in items"
                    v-slot="{ selected }"
                    :key="index"
                    as="div"
                    class="hidden h-full truncate outline-none xl:block"
                >
                    <button
                        :class="[
                            'h-full w-full truncate border-b-4 px-4 py-2 text-sm font-bold uppercase outline-none',
                            selected
                                ? 'border-sun-yellow text-black'
                                : 'text-deep-blue/50 border-transparent'
                        ]"
                        type="button"
                    >
                        {{ item.label }}
                    </button>
                </Tab>

                <div class="flex-none">
                    <slot name="extra" />
                </div>
            </TabList>

            <TabPanels class="flex-1 overflow-y-auto xl:overflow-y-hidden">
                <UiLoader v-if="loading" class="h-full" />

                <TabPanel v-else class="min-h-full xl:h-full" static as="div">
                    <slot
                        v-if="$slots[`tab-${selectedTab?.value}`]"
                        :name="`tab-${selectedTab?.value}`"
                    />
                    <slot v-else />
                </TabPanel>
            </TabPanels>
        </TabGroup>
    </div>
</template>